import {
  type Project,
  type ProjectSortField,
  type ProjectStatus,
  ProjectStatuses,
  routes,
} from '@pmai/contracts';
import {
  Button,
  CheckboxGroup,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  PageHeader,
  PanelDialog,
  TextField,
} from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { NotesPanel } from '../../entities/notes/notes-panel';
import { useApiQuery } from '../../shared/api/hooks';
import { Can } from '../../shared/auth/session';
import { useEnumLabel } from '../../shared/i18n';
import { useListParams, useSearchBox } from '../../shared/list/use-list-params';
import { usePreference } from '../../shared/preferences';
import { useConfirm } from '../../shared/use-confirm';
import { PROJECT_DERIVED_VIEWS } from '../../entities/projects/derived-views';
import { useProjectColumns } from '../../entities/projects/project-columns';
import { ProjectDialog } from './project-dialog';

const ALL = ProjectStatuses.codes;

/** Sākums — the project list (FR-DASH-001…012). */
export function DashboardPage() {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const navigate = useNavigate();
  const list = useListParams<ProjectSortField>({ sort: 'name', limit: 1000 });
  const search = useSearchBox(list.q, (q) => list.set({ q }));
  const statusPref = usePreference('dashboardStatuses');
  // Never saved → all checked (FR-DASH-004); none checked → all shown.
  const checked: readonly ProjectStatus[] = statusPref.value ?? ALL;
  const filtering = checked.length > 0 && checked.length < ALL.length;
  const projects = useApiQuery(
    routes.projects.list,
    {
      query: {
        q: list.q || undefined,
        statuses: filtering ? checked.join(',') : undefined,
        sort: list.sort,
        order: list.order,
        limit: list.limit,
      },
    },
    { enabled: statusPref.loaded, placeholderData: (previous) => previous },
  );
  const [editing, setEditing] = useState<Project | 'new' | null>(null);
  const [notesOf, setNotesOf] = useState<Project | null>(null);
  const remove = useConfirm(routes.projects.remove, {
    invalidates: [routes.projects.list, ...PROJECT_DERIVED_VIEWS],
    success: 'projects.deleted',
    params: (p: Project) => ({ id: p.id }),
  });
  const c = useProjectColumns();

  return (
    <>
      <PageHeader
        title={t('nav.dashboard')}
        actions={
          <Can perm="projects.edit">
            <Button icon="plus" onClick={() => setEditing('new')}>
              {t('projects.new')}
            </Button>
          </Can>
        }
      />
      <div className="mb-4 flex flex-col gap-4">
        <TextField label={t('common.search')} type="search" placeholder={t('projects.search')} {...search} />
        <CheckboxGroup
          label={t('projects.statusFilter')}
          options={ALL.map((code) => ({ value: code, label: label(ProjectStatuses.byCode(code)) }))}
          value={checked}
          onChange={statusPref.save}
        />
      </div>
      <DataTable
        label={t('nav.dashboard')}
        rows={projects.data?.rows}
        rowKey={(p) => p.id}
        loading={projects.isFetching}
        empty={<EmptyState icon="folder" title={t('projects.empty')} />}
        sort={{ sort: list.sort, order: list.order }}
        onSortChange={(s) => list.set({ sort: s.sort, order: s.order })}
        columns={[
          {
            key: 'number',
            header: t('projects.fields.number'),
            cell: (_p, i) => i + 1,
            className: 'w-10 text-ink-muted',
          },
          c.name,
          c.customer,
          c.status,
          c.startDate,
          c.plannedFinish,
          c.priority,
          c.completion,
          c.updatedAt,
          c.charter,
          {
            key: 'actions',
            header: t('common.actions'),
            align: 'right',
            cell: (p) => (
              <div className="flex justify-end gap-1">
                <IconButton
                  icon="eye"
                  label={`${t('common.view')}: ${p.name}`}
                  onClick={() => void navigate(`/projects/${p.id}`)}
                />
                <IconButton
                  icon="notebook-pen"
                  label={`${t('projects.notes')}: ${p.name}`}
                  onClick={() => setNotesOf(p)}
                />
                <Can perm="projects.edit">
                  <IconButton
                    icon="pencil"
                    label={`${t('common.edit')}: ${p.name}`}
                    onClick={() => setEditing(p)}
                  />
                  <IconButton
                    icon="trash-2"
                    label={`${t('common.delete')}: ${p.name}`}
                    onClick={() => remove.ask(p)}
                  />
                </Can>
              </div>
            ),
          },
        ]}
      />
      <ProjectDialog
        open={editing !== null}
        project={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
      <PanelDialog
        open={notesOf !== null}
        onOpenChange={(o) => !o && setNotesOf(null)}
        title={t('projects.notesTitle', { name: notesOf?.name ?? '' })}
      >
        {notesOf && <NotesPanel owner={{ kind: 'project', id: notesOf.id }} />}
      </PanelDialog>
      <ConfirmDialog
        {...remove.dialog}
        title={t('projects.deleteTitle')}
        message={t('projects.deleteText', { name: remove.target?.name ?? '' })}
        confirmWord="DELETE"
        confirmLabel={t('common.delete')}
      />
    </>
  );
}
